import React from 'react';
import { Calendar, Clock, User, ArrowRight, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { blogPostsData } from '../mockData';

export const Blog = () => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedCategory, setSelectedCategory] = React.useState('All');

  const categories = ['All', 'Data Analytics', 'Data Engineering', 'Artificial Intelligence', 'Cloud Infrastructure'];

  const filteredPosts = blogPostsData.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Blog & Insights
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Expert perspectives on data analytics, technology trends, and business intelligence
          </p>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input 
                type="text"
                placeholder="Search articles..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  size="sm"
                  className={selectedCategory === category ? 'bg-blue-900 hover:bg-blue-800' : ''}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <Badge className="bg-blue-900 text-white">Featured Article</Badge>
          </div>
          
          <Card className="border-0 shadow-2xl overflow-hidden">
            <CardContent className="p-0">
              <div className="grid lg:grid-cols-2">
                <div className="relative h-96 lg:h-auto">
                  <img 
                    src={blogPostsData[0].image}
                    alt={blogPostsData[0].title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                <div className="p-10 lg:p-12 flex flex-col justify-center">
                  <Badge className="w-fit mb-4 bg-blue-100 text-blue-900 hover:bg-blue-100">
                    {blogPostsData[0].category}
                  </Badge>
                  <h2 className="text-4xl font-bold text-gray-900 mb-4">
                    {blogPostsData[0].title}
                  </h2>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    {blogPostsData[0].excerpt}
                  </p>
                  <div className="flex items-center gap-6 text-sm text-gray-600 mb-6">
                    <div className="flex items-center gap-2">
                      <User size={16} />
                      <span>{blogPostsData[0].author}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={16} />
                      <span>{blogPostsData[0].date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={16} />
                      <span>{blogPostsData[0].readTime}</span>
                    </div>
                  </div>
                  <Button 
                    className="w-fit bg-blue-900 hover:bg-blue-800 text-white"
                  >
                    Read Article
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* All Posts */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12">
            Latest Articles
          </h2>

          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No articles found matching your search.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Card key={post.id} className="border-gray-200 hover:shadow-xl transition-all duration-300 group overflow-hidden">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-blue-900 text-white">
                        {post.category}
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-900 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                      <div className="flex items-center gap-1">
                        <User size={14} />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{post.date}</span>
                      <Button 
                        variant="link" 
                        className="text-blue-900 p-0 group-hover:gap-2 transition-all"
                      >
                        Read More
                        <ArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" size={16} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Subscribe to Our Newsletter
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Get the latest insights on data analytics and technology trends delivered to your inbox
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <Input 
              type="email"
              placeholder="Enter your email"
              className="bg-white flex-1"
            />
            <Button 
              size="lg"
              className="bg-white text-blue-900 hover:bg-gray-100 font-semibold whitespace-nowrap"
            >
              Subscribe
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </div>
          <p className="text-sm text-blue-200 mt-4">
            No spam. Unsubscribe anytime.
          </p>
        </div>
      </section>

      {/* Topics Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Popular Topics
            </h2>
            <p className="text-xl text-gray-600">
              Explore articles by topic
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {[
              'Data Analytics', 'Machine Learning', 'Cloud Computing', 'Business Intelligence',
              'Data Engineering', 'Artificial Intelligence', 'Data Visualization', 'Big Data',
              'ETL Processes', 'Data Governance', 'Predictive Analytics', 'Data Strategy'
            ].map((topic, index) => (
              <button
                key={index}
                className="px-6 py-3 bg-gray-100 hover:bg-blue-900 hover:text-white rounded-full text-gray-700 font-medium transition-colors duration-300"
              >
                {topic}
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
